"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useSyncExternalStore } from "react";

import { TbMonogram } from "@/components/brand/tb-monogram";
import { PANEL_LAYER_CLASS } from "@/lib/host-panel/layer-stack";
import {
  VEIL_COVER_MS,
  VEIL_DOCUMENT_STALL_CEILING_MS,
  VEIL_FONT_WAIT_MS,
  VEIL_STALL_CEILING_MS,
  announcePageTransition,
  getPageVeil,
  getServerPageVeil,
  isHistoryTraversal,
  notePageVeilPathname,
  revealPageVeil,
  scrollPageToTop,
  subscribePageVeil,
} from "@/lib/motion/page-transition";
import { cn } from "@/lib/utils";

/**
 * The page loader — the monogram as the bridge between pages (`#tbPgLoader` and
 * `nav-transition.js` in the final design package): a porcelain veil with the monogram breathing
 * in brand green. Leaving a page covers it; the next page is revealed once it has painted and its
 * fonts have settled. A full load starts covered.
 *
 * Which wait this is: the passage between pages, including JS and CSS arriving on a full load.
 * Content still arriving inside a page is the skeletons' job — a route with its own `loading.tsx`
 * commits at once, so the veil lifts onto the skeleton.
 *
 * One departure from the prototype: it marks the container `aria-hidden`, which silences its own
 * status inside. Here the status is exposed, with the same "Carregando".
 *
 * Two rules of the person's (2026-10-04): the veil never scrolls — nor lets the page under it
 * scroll, which would move it before it is revealed — and a page reached by a new navigation is
 * revealed at its top. Going back or forward is the exception: that page returns where it was.
 */
export function PageLoader() {
  const veil = useSyncExternalStore(subscribePageVeil, getPageVeil, getServerPageVeil);
  const pathname = usePathname();
  const veilRef = useRef<HTMLDivElement>(null);
  const isCovering = veil.phase === "arrival" || veil.phase === "covering";

  useEffect(() => {
    notePageVeilPathname(pathname);
    return () => notePageVeilPathname(null);
  }, [pathname]);

  // Arrival: this effect runs once the page has hydrated — the part of "painted" a script can
  // observe — and the reveal waits for the fonts as the prototype does.
  useEffect(() => {
    if (veil.phase !== "arrival") return;
    return afterSettled(0, revealPageVeil);
  }, [veil.phase]);

  // Leaving: hold the cover until the next page has committed and the veil is fully in.
  useEffect(() => {
    if (veil.phase !== "covering") return;

    const elapsed = performance.now() - veil.startedAt;
    if (pathname === veil.from) {
      // Still on the page left: the next one is on its way. Only a navigation that died lets the
      // veil go — a slow page never does (`VEIL_STALL_CEILING_MS`).
      const ceiling = veil.carrier === "document" ? VEIL_DOCUMENT_STALL_CEILING_MS : VEIL_STALL_CEILING_MS;
      const stall = setTimeout(revealPageVeil, Math.max(0, ceiling - elapsed));
      return () => clearTimeout(stall);
    }

    // The next page has committed under the veil: it starts at its top, unless it came back
    // through history. Done here, while nothing can be seen, so the jump never shows.
    if (!isHistoryTraversal()) scrollPageToTop();

    return afterSettled(Math.max(0, VEIL_COVER_MS - elapsed), revealPageVeil);
  }, [veil, pathname]);

  // While the veil covers, no scroll gets through: not by the wheel or a finger over it (a
  // listener that is not passive, or it could not cancel), nor by the keys that scroll a page.
  // Nothing about the page's own overflow changes, so the layout does not move.
  useEffect(() => {
    const node = veilRef.current;
    if (!isCovering || node === null) return;

    const swallow = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      // A field keeps its own keys: a space typed into it is text, not a scroll.
      if (target?.isContentEditable || target?.closest?.("input, textarea, select")) return;
      if (SCROLL_KEYS.has(event.key)) event.preventDefault();
    };

    node.addEventListener("wheel", swallow, { passive: false });
    node.addEventListener("touchmove", swallow, { passive: false });
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      node.removeEventListener("wheel", swallow);
      node.removeEventListener("touchmove", swallow);
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [isCovering]);

  // Leaving by a plain `<a>` is a full document load, which the router never hears about. Seen
  // at the end of propagation, a click nobody cancelled is the browser's own navigation (`<Link>`
  // always cancels its click), so the veil covers the page before it unloads, as the prototype
  // does; the next document arrives already covered.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if ((anchor.target !== "" && anchor.target !== "_self") || anchor.hasAttribute("download")) return;
      if (anchor.origin !== window.location.origin) return;

      announcePageTransition(anchor.href, "push", "document");
    };
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, []);

  // Back/forward into a page kept in memory: it comes back with whatever veil it left with.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) revealPageVeil();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  if (veil.phase === "idle") return null;

  return (
    <div
      ref={veilRef}
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-phase={veil.phase}
      className={cn(
        "tb-page-loader fixed inset-0 grid place-items-center",
        PANEL_LAYER_CLASS.pageLoader,
      )}
    >
      <span className="sr-only">Carregando</span>
      {/* The crest is not square (654.87 × 592.36): in the 92px box it centres without stretching. */}
      <TbMonogram className="tb-page-loader-monogram" />
    </div>
  );
}

/** The keys a browser scrolls the page with. */
const SCROLL_KEYS = new Set([" ", "PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown"]);

/**
 * Runs `fn` after `delayMs`, once web fonts have settled (or `VEIL_FONT_WAIT_MS` passed), and two
 * frames later — the prototype's `proxQuadro`, with its timeout race because rAF does not fire
 * in a background tab. Returns the cancel.
 */
function afterSettled(delayMs: number, fn: () => void): () => void {
  let cancelled = false;
  const run = () => {
    if (!cancelled) fn();
  };

  const timer = setTimeout(() => {
    const fonts = document.fonts?.ready ?? Promise.resolve();
    const fontWait = new Promise<void>((resolve) => setTimeout(resolve, VEIL_FONT_WAIT_MS));
    void Promise.race([fonts, fontWait]).then(() => {
      let done = false;
      const once = () => {
        if (done) return;
        done = true;
        run();
      };
      requestAnimationFrame(() => requestAnimationFrame(once));
      setTimeout(once, 40);
    });
  }, delayMs);

  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}
